import { useEffect, useRef, useState } from 'react';
import { QueueTable, useQueueGrid } from '@components/QueueTable';
import { EventDrawer } from '@components/EventDrawer';
import { openEvent } from '@lib/open-mode';
import { cn } from '@/lib/cn';
import { memberSearch, pcpSearch, startCall, type CallStartInput } from '@lib/care-api';
import { useEventQueue } from '@lib/useEventQueue';
import { clientDef, useClient } from '@lib/clients';
import { fmtMemberId } from '@lib/member-id';
import type { EventRecord, MemberCandidate, PcpCandidate } from '@lib/care-model';

/**
 * LiveCall - Flow B (live inbound call, nav 02). Marny answers a routed call and sets up the
 * caller in one screen: caller type (Member / PCP office / Other), phone, the matching typeahead
 * (member, or PCP office + the member being discussed), and what the caller wants. "Start call
 * record" mints ONE inbound Voice event via POST /api/care/call-start - already claimed to her and
 * in_progress - then opens the standard EventDrawer on it (the full action surface: actions,
 * contact log, attachments). Below, "Today's live calls" lists ONLY the call-start mints from today
 * (never poller-ingested voicemails) so a just-closed drawer is one click away.
 */

type CallerType = CallStartInput['callerType'];

const CALLER_CHIPS: { key: CallerType; label: string }[] = [
  { key: 'member', label: 'Member' },
  { key: 'pcp', label: 'PCP office' },
  { key: 'other', label: 'Other' },
];

/** Shared debounced-typeahead plumbing (the Lookup/PcpLookup pattern): latest-wins via a seq id. */
function useDebouncedSearch<T>(q: string, run: (term: string) => Promise<T[]>): { hits: T[]; busy: boolean } {
  const [hits, setHits] = useState<T[]>([]);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  useEffect(() => {
    const term = q.trim();
    const myReq = ++seq.current;
    if (term.length < 2) {
      setBusy(false);
      setHits([]);
      return;
    }
    setBusy(true);
    const t = setTimeout(() => {
      void run(term)
        .then((r) => {
          if (myReq !== seq.current) return; // superseded; drop this result
          setHits(r);
        })
        .catch(() => {
          if (myReq !== seq.current) return;
          setHits([]);
        })
        .finally(() => {
          if (myReq === seq.current) setBusy(false);
        });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  return { hits, busy };
}

/** Member typeahead: pick a 26fobt member (PHI-light candidates; thin audit server-side). */
function MemberPicker({
  label,
  picked,
  onPick,
}: {
  label: string;
  picked: MemberCandidate | null;
  onPick: (m: MemberCandidate | null) => void;
}) {
  const [q, setQ] = useState('');
  const { hits, busy } = useDebouncedSearch(q, (term) => memberSearch(term, 8).then((r) => r.candidates));

  if (picked) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="micro muted">{label}</span>
        <span className="pill pill-success">
          {picked.full_name || 'member'} #{fmtMemberId(picked.member_id)}
        </span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onPick(null)}>
          Clear
        </button>
      </div>
    );
  }
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="micro muted">{label}</span>
        <input
          className="text-input"
          type="search"
          aria-label={label}
          style={{ maxWidth: 320 }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Name / member ID / phone"
        />
        {busy && <span className="muted small">searching...</span>}
      </div>
      {hits.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {hits.map((m) => (
            <button
              key={m.member_id}
              type="button"
              className="chip"
              onClick={() => {
                onPick(m);
                setQ('');
              }}
            >
              {m.full_name || 'member'} #{fmtMemberId(m.member_id)}
              {m.dob ? ` ${'\u00B7'} ${m.dob}` : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** PCP typeahead over the 26fobt directory (reuses the pcp-search client from PCP lookup). */
function PcpPicker({
  picked,
  onPick,
}: {
  picked: PcpCandidate | null;
  onPick: (p: PcpCandidate | null) => void;
}) {
  const [q, setQ] = useState('');
  const { hits, busy } = useDebouncedSearch(q, (term) => pcpSearch(term, 8).then((r) => r.pcps));

  if (picked) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="micro muted">PCP office</span>
        <span className="pill pill-accent">
          {picked.name || 'PCP'}
          {picked.npi ? ` ${'\u00B7'} NPI ${picked.npi}` : ''}
        </span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onPick(null)}>
          Clear
        </button>
      </div>
    );
  }
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="micro muted">PCP office</span>
        <input
          className="text-input"
          type="search"
          aria-label="Search PCP offices"
          style={{ maxWidth: 320 }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="PCP name / NPI / fax digits"
        />
        {busy && <span className="muted small">searching...</span>}
      </div>
      {hits.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {hits.map((p, i) => (
            <button
              key={`${p.npi || p.name}#${i}`}
              type="button"
              className="chip"
              onClick={() => {
                onPick(p);
                setQ('');
              }}
            >
              {p.name || 'PCP'}
              {p.npi ? ` ${'\u00B7'} ${p.npi}` : ''}
              {p.city ? ` ${'\u00B7'} ${p.city}` : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** True when an ISO timestamp lands on the local calendar date of `now`. */
function isToday(iso: string, now: Date): boolean {
  const d = new Date(iso);
  return !Number.isNaN(d.getTime()) && d.toDateString() === now.toDateString();
}

/**
 * True for a call-start-minted record. The mint's subject is always one of the three 'Live call...'
 * fingerprints (call-start.ts); poller-ingested voicemails are also inbound Voice but carry the
 * email subject, so without this gate they would read as live calls Marny answered.
 */
function isLiveCallMint(subject: string): boolean {
  return subject.startsWith('Live call');
}

/** Fresh per-form idempotency key; regenerated only after a successful mint, so a retry after an
 * HTTP 500 resends the SAME key and the API returns the already-durable record instead of a duplicate. */
function newRequestId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `rq-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function LiveCall() {
  // The client chosen in the header: every search and match on this page is made for it.
  const clientName = clientDef(useClient()).displayName;
  const [callerType, setCallerType] = useState<CallerType>('member');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [member, setMember] = useState<MemberCandidate | null>(null);
  const [pcp, setPcp] = useState<PcpCandidate | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [flash, setFlash] = useState('');
  const [starting, setStarting] = useState(false);

  // Today's live calls: the shared inbound queue hook, filtered client-side to call-start mints
  // (the 'Live call...' subject fingerprint) + today, so a call she just took is one click from
  // reopening in the drawer. Poller voicemails (also inbound Voice) never belong in this list.
  const { data, loading, reload } = useEventQueue({ direction: 'inbound' });
  const now = new Date();
  const todaysCalls = (data?.items ?? []).filter(
    (r) => r.channel === 'Voice' && isLiveCallMint(r.subject) && isToday(r.receivedTs, now),
  );
  // Row 356: the shared grid; the server's order (newest first) until she sorts.
  const grid = useQueueGrid({
    table: 'live-calls',
    items: todaysCalls,
    slicers: ['status', 'match', 'handledBy'],
  });

  const phoneDigits = phone.replace(/\D+/g, '');
  const phoneInvalid = phone.trim() !== '' && phoneDigits.length < 10;
  const requestIdRef = useRef(newRequestId());

  const start = async () => {
    setFlash('');
    if (phoneInvalid) {
      setFlash('Phone must contain at least 10 digits (or leave it blank).');
      return;
    }
    setStarting(true);
    try {
      const input: CallStartInput = { callerType, requestId: requestIdRef.current };
      if (phone.trim()) input.phone = phone.trim();
      if (note.trim()) input.note = note.trim();
      if (callerType !== 'other' && member) input.memberId = member.member_id;
      if (callerType === 'pcp' && pcp) {
        if (pcp.npi) input.pcpNpi = pcp.npi;
        if (pcp.name) input.pcpName = pcp.name;
      }
      const r = await startCall(input);
      const created = (r.data as { event?: EventRecord }).event;
      if (r.ok && created) {
        requestIdRef.current = newRequestId(); // this form is done; the next call gets its own key
        setFlash('Call logged and sent to the Inbound queue (In progress), claimed to you; it also appears in "Today\'s live calls" below. Audio is NOT recorded by this app; if Teams records the call, the recording attaches to this row automatically afterward.');
        setPhone('');
        setNote('');
        setMember(null);
        setPcp(null);
        await reload();
        // The call just logged opens HERE, in the side panel, whatever the open-cases switch says: it
        // is the call in progress, opened by the form after a round trip, not a row she clicked.
        setSelected(created.eventId);
      } else {
        const err = (r.data as { error?: string }).error;
        setFlash(`Could not log the call (HTTP ${r.status}${err ? `: ${err}` : ''}).`);
      }
    } finally {
      setStarting(false);
    }
  };

  const onDrawerChanged = () => {
    // No status-tab follow here (the list has no tabs); refresh so the row reflects the new state.
    reload();
  };

  return (
    <div className="page-pad">
      <header>
        <div className="section-eyebrow">Member Care {'\u00B7'} Flow B</div>
        <h1 className="section-title">
          Live <em>call</em>
        </h1>
        <div className="section-rule" />
        <p className="section-lede">
          A live inbound call was routed to you. Set up the caller - member or PCP office - and log
          the call: it lands claimed to you and in progress, with the full drawer (actions, contact
          log, attachments) open for the rest of the call. Logging does not record audio - recording
          happens in Teams on your machine, and any Teams recording attaches to the row automatically
          after the call.
        </p>
      </header>

      <div className="card" style={{ marginBottom: 18, padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <span className="micro muted">Caller</span>
          {CALLER_CHIPS.map((c) => (
            <button
              key={c.key}
              type="button"
              className={cn('chip', callerType === c.key && 'selected')}
              onClick={() => {
                setCallerType(c.key);
                if (c.key === 'other') {
                  setMember(null);
                  setPcp(null);
                }
                if (c.key === 'member') setPcp(null);
              }}
            >
              {c.label}
            </button>
          ))}
          <input
            className="text-input"
            type="tel"
            aria-label="Caller phone"
            style={{ marginLeft: 'auto', maxWidth: 220 }}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Caller phone"
          />
        </div>

        {phoneInvalid && (
          <div className="note warn" style={{ marginBottom: 12 }}>
            Phone must contain at least 10 digits (or leave it blank).
          </div>
        )}

        {callerType === 'member' && (
          <div style={{ marginBottom: 12 }}>
            <MemberPicker label="Member" picked={member} onPick={setMember} />
            <div className="muted small" style={{ marginTop: 6 }}>
              No match handy? Start with just the phone - the drawer's Update matches it against the
              entry's own campaign roster in {clientName}'s database (phone1/phone2) once the record exists.
            </div>
          </div>
        )}

        {callerType === 'pcp' && (
          <div style={{ display: 'grid', gap: 12, marginBottom: 12 }}>
            <PcpPicker picked={pcp} onPick={setPcp} />
            <MemberPicker label="Member discussed (optional)" picked={member} onPick={setMember} />
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <textarea
            className="text-input"
            aria-label="What the caller wants"
            style={{ width: '100%', minHeight: 64, resize: 'vertical' }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What the caller wants..."
          />
        </div>

        <button type="button" className="btn btn-teal" disabled={starting} onClick={() => void start()}>
          {starting ? 'Logging...' : 'Log this call'}
        </button>
      </div>

      {flash && <div className="note" style={{ marginBottom: 14 }}>{flash}</div>}

      <div className="section-eyebrow" style={{ marginBottom: 8 }}>
        Today's live calls{todaysCalls.length ? ` (${todaysCalls.length})` : ''}
      </div>
      <QueueTable
        grid={grid}
        selectedId={selected}
        loading={loading}
        onSelect={(id) => openEvent(id, setSelected)}
        emptyLabel="No live calls taken today."
        noun="calls"
      />

      {selected && (
        <EventDrawer
          eventId={selected}
          hasAudioHint={todaysCalls.find((r) => r.eventId === selected)?.hasAudio ?? false}
          onClose={() => setSelected(null)}
          onChanged={onDrawerChanged}
        />
      )}
    </div>
  );
}
